import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HdGateInfoRequestedContext } from '../hd-gate-info-requested-context.model';
import { HdGateInfoContext } from '../hd-gate-info-context.model';
import { formatHdGateActivationDescriptors } from '../hd-gate-info-requested-labels.util';
import { HdGateInfoCheatSheetComponent, HdGateInfoCheatSheetVisualState } from './hd-gate-info-cheat-sheet.component';
import { HdPlanetInfoService } from '../../hd-planet-info.service';
import { HdContentLanguageService } from '../../hd-content-language.service';
import { getHdPlanetDisplayConfig } from '../../planet-grid/hd-planet-display-config';

/** Eén planeet-glyph in de compacte activatie-rij. */
interface PlanetGlyphViewModel {
  readonly key: string;
  /** Astronomicon-glyph van de planeet. */
  readonly char: string;
  /** Volledige (vertaalde) planeet-naam — getoond als hover-tooltip. */
  readonly name: string;
  /** Design-activatie → glyph in het rood (vs zwart voor personality). */
  readonly isDesign: boolean;
}

/**
 * Compacte ("small") inhoud van het floating gate-info panel. Toont:
 *  1. Een knop om naar de volledige ("full") weergave te wisselen.
 *  2. Een horizontale rij planeet-symbolen (astronomicon-glyphs) van de
 *     planeten die deze poort activeren — volledige naam op hover. Bij geen
 *     activatie verschijnt een icoon met de passende uitleg als tooltip.
 *  3. De cheat-sheet kaart van dezelfde poort.
 *
 * De gedetailleerde activatie-/invloed-regels staan bewust enkel in de
 * full-variant (zie spec §2.3 / Q1). Glyph-namen volgen de content-taal-override.
 */
@Component({
  selector: 'hd-gate-info-small',
  templateUrl: './hd-gate-info-small.component.html',
  styleUrl: './hd-gate-info-small.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, TranslocoModule, HdGateInfoCheatSheetComponent],
})
export class HdGateInfoSmallComponent {
  readonly context = input.required<HdGateInfoRequestedContext>();

  /** Vraagt de router-variant om naar de full-weergave te wisselen. */
  readonly expandRequested = output<void>();

  private readonly planetInfoService = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly transloco = inject(TranslocoService);

  /** Planeet-glyphs van alle activaties van deze poort (leeg = geen activatie). */
  protected readonly planetGlyphs = computed<readonly PlanetGlyphViewModel[]>(() => {
    const ctx = this.context();
    const lang = this.contentLang.effectiveLang();
    const planetTranslations = this.planetInfoService.planetTranslationsAllIn(lang);
    return formatHdGateActivationDescriptors(ctx)
      .filter(descriptor => descriptor.fallbackKind === null && descriptor.planetKey !== null)
      .map<PlanetGlyphViewModel>(descriptor => {
        const config = getHdPlanetDisplayConfig(descriptor.planetKey!);
        return {
          key: descriptor.key,
          char: config.astronomiconChar,
          name: planetTranslations[descriptor.planetKey!]?.name ?? config.label,
          isDesign: descriptor.side === 'design',
        };
      });
  });

  /**
   * Tooltip-tekst voor de "geen activatie"-indicator, of `null` wanneer er
   * wél planeet-glyphs zijn. Volgt de content-taal-override.
   */
  protected readonly noActivationLabel = computed<string | null>(() => {
    if (this.planetGlyphs().length > 0) return null;
    const ctx = this.context();
    const lang = this.contentLang.effectiveLang();
    const key = ctx.isDummyActiveViaEditor
      ? 'humanDesign.gateInfoRequested.dummyActiveViaEditor'
      : 'humanDesign.gateInfoRequested.noPlanetActivation';
    return this.transloco.translate<string>(key, undefined, lang);
  });

  /**
   * Label van de "Volledige info"-knop — volgt de content-taal-override van
   * de editor-pagina (de `| transloco`-pipe kijkt enkel naar de globale
   * app-taal en zou hier dus de override niet honoreren). Zie `AGENTS.md` §13
   * voor de algemene i18n-conventie achter dit patroon.
   */
  protected readonly expandLabel = computed<string>(() =>
    this.transloco.translate<string>('humanDesign.gateInfo.expand', undefined, this.contentLang.effectiveLang()),
  );

  protected readonly cheatSheetContext = computed<HdGateInfoContext>(() => {
    const ctx = this.context();
    return { gate: ctx.gate, activations: ctx.activations };
  });

  protected readonly cheatSheetVisualState = computed<HdGateInfoCheatSheetVisualState>(
    () => this.context().visualState ?? 'defined',
  );
}
